/* eslint-disable no-unused-vars */
import React from 'react';
import ReactDOM from 'react-dom';
import { withRouter } from 'react-router-dom';
import UserProfile from 'components/partials/layout/UserProfile';
import ImpersonationBadge from 'components/ImpersonationBadge';
import { getUserRole } from 'redux/profile/selector';
import { connect } from 'react-redux';
import {
  hasPerformanceFunctionalityConsent,
  hideCrispChat,
  isCrispWidgetInDom,
  isSecuritiConsentReady,
  isUtagLoaded,
  openCookiePreferencesPopup,
  requestCrispTagFromTealium,
  scheduleReloadAfterConsentSave,
} from 'utils/crispConsent';

const CONSENT_POLL_INTERVAL_MS = 250;
const CONSENT_POLL_MAX_ATTEMPTS = 80;
const CRISP_LOAD_TIMEOUT_MS = 8000;
const LOGIN_PATH_PREFIXES = ['/login', '/resetPassword', '/preRegister'];
const CHAT_PLACEHOLDER_ROOT_ID = 'chat-placeholder-root';
// Buttons that persist consent: modal "Save Changes" plus banner accept/deny/dismiss.
const CONSENT_SAVE_BUTTON_SELECTOR =
  '.cmp-save-btn, .cc-allow, .cc-deny, .cc-dismiss';

const getChatPlaceholderRoot = () =>
  document.getElementById(CHAT_PLACEHOLDER_ROOT_ID);

class Topbar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showChatPlaceholder: false,
    };
    this.isMountedFlag = false;
    this.consentPollAttempts = 0;
    this.consentPollTimer = null;
    this.chatWaitStartedAt = 0;
    this.handleConsentPreferencesSaveClick = this.handleConsentPreferencesSaveClick.bind(
      this,
    );
    this.pollForSecuritiConsent = this.pollForSecuritiConsent.bind(this);
    this.openCookiePreferences = this.openCookiePreferences.bind(this);
  }

  isAuthenticatedRoute(pathname = this.props.location.pathname) {
    return !LOGIN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  }

  clearConsentPollTimer() {
    if (this.consentPollTimer) {
      clearTimeout(this.consentPollTimer);
      this.consentPollTimer = null;
    }
  }

  hasChatWaitTimedOut() {
    return Date.now() - this.chatWaitStartedAt >= CRISP_LOAD_TIMEOUT_MS;
  }

  setChatPlaceholderVisible(visible) {
    if (this.isMountedFlag && this.state.showChatPlaceholder !== visible) {
      this.setState({ showChatPlaceholder: visible });
    }
  }

  syncChatUi() {
    if (!this.isAuthenticatedRoute()) {
      this.setChatPlaceholderVisible(false);
      hideCrispChat();
      return;
    }

    const hasConsent = hasPerformanceFunctionalityConsent();
    const crispInDom = isCrispWidgetInDom();

    // utag owns Crisp. If the widget is here or consent allows it, never
    // mount a React portal next to those body nodes.
    if (hasConsent || crispInDom) {
      // The Tealium tag only runs on `view` events and skips /login, so after a
      // client-side login it has to be asked for one more view.
      if (!crispInDom && requestCrispTagFromTealium()) {
        // Give the tag a fresh window to inject the widget before settling.
        this.consentPollAttempts = 0;
        this.chatWaitStartedAt = Date.now();
      }

      this.setChatPlaceholderVisible(false);
      return;
    }

    const consentReady = isSecuritiConsentReady();
    const utagReady = isUtagLoaded();
    const timedOut = this.hasChatWaitTimedOut();

    // Wait for Tealium/Crisp (or the timeout) before showing the fake button.
    if (!timedOut && (!consentReady || !utagReady)) {
      this.setChatPlaceholderVisible(false);
      return;
    }

    hideCrispChat();
    this.setChatPlaceholderVisible(true);
  }

  openCookiePreferences() {
    openCookiePreferencesPopup();
  }

  handleConsentPreferencesSaveClick(event) {
    const target = event.target;

    if (
      !(target instanceof Element) ||
      !target.closest(CONSENT_SAVE_BUTTON_SELECTOR)
    ) {
      return;
    }

    scheduleReloadAfterConsentSave();
  }

  isChatLoadSettled() {
    if (this.consentPollAttempts >= CONSENT_POLL_MAX_ATTEMPTS) {
      return true;
    }

    if (this.hasChatWaitTimedOut()) {
      return true;
    }

    const crispInDom = isCrispWidgetInDom();

    if (!this.isAuthenticatedRoute()) {
      return crispInDom;
    }

    if (crispInDom) {
      return true;
    }

    // Consent is allowed: keep polling until utag injects the Crisp widget.
    if (hasPerformanceFunctionalityConsent()) {
      return false;
    }

    return isSecuritiConsentReady() && isUtagLoaded();
  }

  pollForSecuritiConsent() {
    this.clearConsentPollTimer();

    if (!this.isMountedFlag) {
      return;
    }

    this.syncChatUi();

    if (this.isChatLoadSettled()) {
      return;
    }

    this.consentPollAttempts += 1;
    this.consentPollTimer = setTimeout(
      this.pollForSecuritiConsent,
      CONSENT_POLL_INTERVAL_MS,
    );
  }

  renderChatPlaceholder() {
    const host = getChatPlaceholderRoot();

    if (!host) {
      return null;
    }

    const show = this.state.showChatPlaceholder && this.isAuthenticatedRoute();

    return ReactDOM.createPortal(
      <div className="kt-chat-placeholder-host">
        {show ? (
          <button
            type="button"
            className="kt-chat-placeholder-button"
            aria-label="Chat"
            onClick={this.openCookiePreferences}
          >
            <svg
              className="kt-chat-placeholder-button__icon"
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  id="kt-chat-placeholder-bubble"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#f3d6d0" />
                </linearGradient>
              </defs>
              <path
                fill="url(#kt-chat-placeholder-bubble)"
                transform="rotate(-8 16 16)"
                d="M8 7h16a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-9l-6 5 1.5-5H8a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3z"
              />
            </svg>
          </button>
        ) : null}
      </div>,
      host,
    );
  }

  componentDidMount() {
    this.isMountedFlag = true;
    // Capture phase so the Securiti SDK cannot stop the event before we see it.
    document.addEventListener(
      'mousedown',
      this.handleConsentPreferencesSaveClick,
      true,
    );
    this.consentPollAttempts = 0;
    this.chatWaitStartedAt = Date.now();
    this.pollForSecuritiConsent();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.consentPollAttempts = 0;
      this.chatWaitStartedAt = Date.now();
      this.pollForSecuritiConsent();
    }
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    this.clearConsentPollTimer();
    document.removeEventListener(
      'mousedown',
      this.handleConsentPreferencesSaveClick,
      true,
    );

    if (this.props.location.pathname.startsWith('/login')) {
      window.location.reload(false);
    }
  }

  render() {
    return (
      <div className="kt-header__topbar">
        <ImpersonationBadge />
        <UserProfile showAvatar={false} showHi={true} showBadge={true} />
        {this.renderChatPlaceholder()}
      </div>
    );
  }
}

const mapStateProps = (state) => ({
  ...getUserRole(state),
});

export default withRouter(connect(mapStateProps)(Topbar));
